import { Component, Prop, h, Element, Host } from '@stencil/core';

const COMPONENT_NAME = 'me-column';

/**
 * A responsive layout column component.
 *
 * Can be used with a grid or flexbox system to define column widths
 * and adapt them at different screen sizes.
 *
 * Allows passing through standard HTML attributes and custom classes.
 */
@Component({
  tag: 'me-column',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Column {
  /** Reference to the host element (<me-column>) */
  @Element() host: HTMLElement;

  /**
   * The HTML tag used for rendering the column.
   * @default 'div'
   */
  @Prop() tag: 'div' | 'aside' = 'div';

  /**
   * Number of columns to span in default layout (1–12 or 'auto').
   * Adds class like `me-column--span-6`
   */
  @Prop() span?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12' | 'auto';

  /**
   * Number of columns to span on medium screens (768px-960px).
   * Adds class like `me-column--span-md-12`
   */
  @Prop() spanMd?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12' | 'auto';

  /**
   * Number of columns to span on large screens (960px+).
   * Adds class like `me-column--span-lg-12`
   */
  @Prop() spanLg?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12' | 'auto';

  /** Combines system classes with host-defined custom classes */
  private getMergedClasses(): string {
    const isValid = (val?: string) => !!val && val !== 'undefined' && val !== 'null';

    // Only include custom classes that do not start with 'me-column'
    const hostClass = (this.host.getAttribute('class') ?? '')
      .split(' ')
      .filter(c => c && !c.startsWith('me-column'))
      .join(' ');

    const baseClasses = [
      COMPONENT_NAME,
      isValid(this.span) ? `${COMPONENT_NAME}--span-${this.span}` : null,
      isValid(this.spanMd) ? `${COMPONENT_NAME}--span-md-${this.spanMd}` : null,
      isValid(this.spanLg) ? `${COMPONENT_NAME}--span-lg-${this.spanLg}` : null,
      hostClass || null,
    ];

    return baseClasses.filter(Boolean).join(' ').trim();
  }

  render() {
    const className = this.getMergedClasses();

    return (
      <Host class={className}>
        <slot></slot>
      </Host>
    );
  }
}
